// layers/fetchLayers.ts

import { 
  CARTO_API, 
  CARTO_TOKEN, 
  TABLE_ISO, 
  TABLE_POP, 
  TABLE_MOBILITY,
  MetroOrigin,
} from "../config/carto";

// Réexporter MetroOrigin pour qu'il soit disponible depuis ce fichier
export type { MetroOrigin };

async function query(q: string) {
  const res = await fetch(CARTO_API, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${CARTO_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ q }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || JSON.stringify(data));
  return data;
}

// ──────────────────────────────────────────────────────────────
// MÉTROS - DEPUIS india_hexTable_052025_short
// ──────────────────────────────────────────────────────────────

export async function fetchMetroOrigins(): Promise<MetroOrigin[]> {
  console.log('🔄 Chargement des métros depuis india_hexTable_052025_short...');
  
  const data = await query(`
    SELECT DISTINCT
      metro_origin,
      airport_lat_origin as lat,
      airport_long_origin as lng
    FROM \`${TABLE_MOBILITY}\`
    WHERE metro_origin IS NOT NULL
      AND metro_origin != ''
      AND gcd_nm > 100
    ORDER BY metro_origin
  `);
  
  console.log(`✅ ${data.rows?.length || 0} métros chargés depuis la table`);
  
  return data.rows.map((row: any) => ({
    metro_origin: row.metro_origin || '',
    lat: parseFloat(row.lat) || 0,
    lng: parseFloat(row.lng) || 0,
  }));
}

// ──────────────────────────────────────────────────────────────
// RECHERCHE DE MÉTROS
// ──────────────────────────────────────────────────────────────

export async function searchMetroOrigins(searchQuery: string): Promise<MetroOrigin[]> {
  if (!searchQuery || searchQuery.length < 2) return [];
  
  console.log(`🔍 Recherche de métros: "${searchQuery}"`);
  
  const data = await query(`
    SELECT DISTINCT
      metro_origin,
      airport_lat_origin as lat,
      airport_long_origin as lng
    FROM \`${TABLE_MOBILITY}\`
    WHERE metro_origin IS NOT NULL
      AND metro_origin != ''
      AND gcd_nm > 100
      AND LOWER(metro_origin) LIKE '%${searchQuery.toLowerCase()}%'
    ORDER BY metro_origin
    LIMIT 10
  `);
  
  console.log(`✅ ${data.rows?.length || 0} résultats trouvés`);
  
  return data.rows.map((row: any) => ({
    metro_origin: row.metro_origin || '',
    lat: parseFloat(row.lat) || 0,
    lng: parseFloat(row.lng) || 0,
  }));
}

// ──────────────────────────────────────────────────────────────
// MOBILITÉ HEX - AVEC FILTRE OPTIONNEL PAR METRO
// ──────────────────────────────────────────────────────────────

export async function fetchMobilityHex(metroOrigin?: string) {
  let whereClause = '';
  
  if (metroOrigin) {
    whereClause = `
      WHERE metro_origin = '${metroOrigin}'
        AND gcd_nm > 100
    `;
    console.log(`🔍 Filtrage des données pour: ${metroOrigin}`);
  }
  
  const data = await query(`
    SELECT 
      h3_destination, 
      SUM(trips_calibrated_weekly) as trips_weekly, 
      SUM(trips_calibrated) as trips_total, 
      MAX(destination_city) as destination_city, 
      MAX(destination_state) as destination_state 
    FROM \`${TABLE_MOBILITY}\`
    ${whereClause}
    GROUP BY h3_destination
  `);
  
  console.log(`✅ ${data.rows?.length || 0} hexagones chargés${metroOrigin ? ` pour ${metroOrigin}` : ''}`);
  return data.rows || [];
}

// ──────────────────────────────────────────────────────────────
// ISOCHRONES
// ──────────────────────────────────────────────────────────────

export async function fetchAllAirports() {
  const data = await query(`
    SELECT DISTINCT
      airport_id,
      airport_name,
      latitude,
      longitude
    FROM \`${TABLE_ISO}\`
    WHERE NOT airport_id IN ('VEKA', 'VI40')
    AND airport_id IS NOT NULL
  `);
  return data.rows;
}

export async function fetchIsochrones(time: number, airportStatus: string[]) {
  const statusList = airportStatus.map((s) => `'${s}'`).join(",");
  
  const data = await query(`
    SELECT
      airport_id,
      airport_name,
      airport_type,
      isochrone_range,
      isochrone_geojson
    FROM \`${TABLE_ISO}\`
    WHERE airport_type IN (${statusList})
    AND isochrone_range = ${time}
    AND NOT airport_id IN ('VEKA', 'VI40')
    AND airport_id IS NOT NULL
    AND isochrone_geojson IS NOT NULL
  `);
  
  return data.rows.map((row: any) => ({
    ...row,
    isochrone_geojson: typeof row.isochrone_geojson === 'string' 
      ? JSON.parse(row.isochrone_geojson) 
      : row.isochrone_geojson
  }));
}

// ──────────────────────────────────────────────────────────────
// POPULATION
// ──────────────────────────────────────────────────────────────

export async function fetchPopulationInIsochrones(
  time: number,
  airportStatus: string[]
) {
  const statusList = airportStatus.map((s) => `'${s}'`).join(",");
  const data = await query(`
    SELECT
      h3_res6,
      population,
      Pop_Assignment
    FROM \`${TABLE_POP}\`
    WHERE range_mins <= ${time}
    AND Pop_Assignment IN (${statusList})
  `);
  return data.rows;
}

export async function fetchTotalPop(time: number, airportStatus: string[]) {
  const statusList = airportStatus.map((s) => `'${s}'`).join(",");
  const data = await query(`
    SELECT SUM(population) as total_pop
    FROM \`${TABLE_POP}\`
    WHERE range_mins <= ${time}
    AND Pop_Assignment IN (${statusList})
  `);
  return data.rows[0];
}